// ===================================================
// GAM Command Center — slash quick-bar favourites + recents
// ===================================================
//
// The quick bar shows a handful of commands, not all 27: a fixed set of
// favourites plus the last few the user actually picked. The full catalog
// lives in the Sidebar's תוספים tab.
//
// Persisted in localStorage, same pattern FieldLibrary already uses for its
// view-mode/position (read after mount, never during render, so SSR and the
// first client render agree).
//
// Two consumers, two window sizes: the floating quick bar wants 3 (it sits
// next to 4 fixed favourites and must stay narrow), while the sidebar's
// תוספים panel wants 4 at the top for "use it again". So the store keeps a
// deeper history than either of them shows and each caller slices what it
// needs — bumping one surface never silently widens the other.

const RECENTS_KEY = 'gam-slash-recents';
/** How many titles we keep on disk. Bigger than any single surface shows. */
const MAX_STORED = 12;
/** Default window — what the floating quick bar has always shown. */
const MAX_RECENTS = 3;
/** What the sidebar's תוספים panel shows in its "לשימוש מהיר" strip. */
export const PANEL_RECENTS = 4;

/** Commands always present in the bar, in display order (English titles). */
export const DEFAULT_FAVOURITES = [
  'Heading 1',
  'Bullet List',
  'Checklist',
  'Table',
] as const;

function readAll(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is string => typeof t === 'string').slice(0, MAX_STORED);
  } catch {
    return [];
  }
}

/** Most-recent-first. `limit` defaults to the quick bar's historical 3. */
export function readRecents(limit: number = MAX_RECENTS): string[] {
  return readAll().slice(0, limit);
}

/** Most-recent-first, de-duplicated, capped. Returns the new list. */
export function pushRecent(title: string, limit: number = MAX_RECENTS): string[] {
  const next = [title, ...readAll().filter((t) => t !== title)].slice(0, MAX_STORED);
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      /* quota / private mode — the bar still works, it just won't remember */
    }
  }
  return next.slice(0, limit);
}

/**
 * Favourites first, then recents that aren't already shown as a favourite —
 * so picking a favourite never duplicates it into the recents half.
 */
export function quickBarTitles(recents: string[]): string[] {
  const favourites = [...DEFAULT_FAVOURITES];
  return [...favourites, ...recents.filter((t) => !favourites.includes(t as typeof favourites[number]))];
}
